/**
 * WorkshopNIntro — talarstödets editor.
 * Facilitatorn skriver råtext, klickar "Generera med AI", redigerar blocken
 * och sparar. Blocken synkas till Firestore och SpeakerView/PublicView
 * konsumerar dem i realtid.
 *
 * Route: /workshopn/:id/intro
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Paper, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TvIcon from '@mui/icons-material/Tv';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { db } from '../../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { callClaude } from '../../../utils/claudeProxy';
import BlockCard from '../components/IntroPresenter/BlockCard';

const accent = '#E05500';

const SYSTEM_PROMPT = `Du är en assistent som hjälper workshopledare att strukturera sina intro-tal.
Du tar emot råtext och returnerar ENDAST ett JSON-objekt — ingen annan text.

JSON-struktur:
{
  "blocks": [
    {
      "id": "block_1",
      "label": "Kort avsnittsnamn (1-3 ord)",
      "keywords": ["Stödord 1", "Stödord 2", "Stödord 3"],
      "speakerText": "Löptext som talaren kan läsa om hen behöver. Behåll talarens röst och formuleringar.",
      "publicTitle": "Rubrik för publik skärm (max 6 ord)",
      "publicQuote": "Valfritt nyckelcitat eller tom sträng"
    }
  ]
}

Riktlinjer:
- Dela in i 5–8 block
- Stödord ska vara konkreta minnesankare, inte abstrakta
- Behåll talarens egna formuleringar i speakerText
- publicTitle ska vara kort och slagkraftig
- publicQuote ska vara den mest minnesvärda meningen i blocket, eller tom
- Returnera BARA JSON, inga förklaringar`;

function newBlock(index) {
  return {
    id: `block_${Date.now()}_${index}`,
    label: '',
    keywords: [],
    speakerText: '',
    publicTitle: '',
    publicQuote: '',
  };
}

export default function WorkshopNIntro() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rawText, setRawText] = useState('');
  const [rawTextVisible, setRawTextVisible] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getDoc(doc(db, 'workshops', id)).then(snap => {
      if (!snap.exists()) return navigate('/workshopn');
      const d = snap.data();
      setSession(d);
      setRawText(d.intro?.rawText || '');
      setRawTextVisible(d.intro?.rawTextVisible ?? false);
      setBlocks(d.intro?.blocks || []);
      setLoading(false);
    });
  }, [id, navigate]);

  const generate = async () => {
    if (!rawText.trim()) return;
    setGenerating(true);
    setError('');
    try {
      const data = await callClaude({
        model: 'claude-sonnet-4-6',
        max_tokens: 4000,
        system: SYSTEM_PROMPT,
        messages: [{ role: 'user', content: rawText }],
      });
      const raw = data.content?.[0]?.text || '';
      const clean = raw.replace(/```json[\s\S]*?```|```[\s\S]*?```/g, s => s.replace(/```json|```/g, '')).trim();
      const parsed = JSON.parse(clean);
      setBlocks(parsed.blocks || []);
    } catch (e) {
      setError('Genereringen misslyckades.');
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await updateDoc(doc(db, 'workshops', id), {
        intro: {
          rawText,
          rawTextVisible,
          blocks,
          currentBlock: 0,
          isLive: false,
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate(`/workshopn/${id}/edit`)} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Talarstöd</Typography>
          {session?.title && (
            <Typography fontSize="0.78rem" color="text.secondary">{session.title}</Typography>
          )}
        </Box>

        {blocks.length > 0 && (
          <>
            <Button
              variant="outlined"
              startIcon={<TvIcon sx={{ fontSize: 16 }} />}
              onClick={() => window.open(`/workshopn/${id}/present-public`, '_blank')}
              sx={{ textTransform: 'none', fontSize: '0.78rem', borderColor: 'divider', color: 'text.secondary', display: { xs: 'none', sm: 'flex' } }}
            >
              Publik skärm
            </Button>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              onClick={() => window.open(`/workshopn/${id}/present?view=speaker`, '_blank')}
              sx={{ textTransform: 'none', fontSize: '0.78rem', borderColor: accent, color: accent }}
            >
              Kör talarstöd
            </Button>
          </>
        )}

        <Button
          variant="contained"
          onClick={save}
          disabled={saving}
          sx={{
            bgcolor: saved ? '#4ade80' : accent,
            '&:hover': { bgcolor: saved ? '#22c55e' : '#C94D00' },
            textTransform: 'none', fontWeight: 600, minWidth: 100,
            transition: 'background-color 0.3s',
          }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : saved ? 'Sparat ✓' : 'Spara'}
        </Button>
      </Box>

      {/* Raw text input */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Typography fontWeight={600} sx={{ flex: 1 }}>Råtext — ditt tal eller dina anteckningar</Typography>
          <Tooltip title={rawTextVisible ? 'Råtexten visas på publik skärm — klicka för att göra privat' : 'Råtexten är privat — klicka för att visa på publik skärm'}>
            <Box
              onClick={() => setRawTextVisible(v => !v)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.4, borderRadius: 1, cursor: 'pointer',
                bgcolor: rawTextVisible ? accent + '18' : 'action.hover',
                border: '1px solid', borderColor: rawTextVisible ? accent + '55' : 'divider',
                color: rawTextVisible ? accent : 'text.secondary',
                '&:hover': { opacity: 0.8 },
                transition: 'all 0.15s',
              }}
            >
              {rawTextVisible ? <LockOpenIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
              <Typography fontSize="0.72rem" fontWeight={700}>
                {rawTextVisible ? 'Synlig för deltagare' : 'Privat'}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
        <Typography fontSize="0.78rem" color="text.secondary" mb={2}>
          Skriv fritt eller klistra in ditt intro-manus. AI:n strukturerar det i block med stödord
          och rubrik till storskärmen.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={6}
          maxRows={24}
          placeholder="Välkomna allihopa. Idag ska vi..."
          value={rawText}
          onChange={e => setRawText(e.target.value)}
          sx={{ mb: 2 }}
        />
        {error && (
          <Typography fontSize="0.8rem" color="error.main" mb={1.5}>{error}</Typography>
        )}
        <Button
          variant="contained"
          startIcon={generating
            ? <CircularProgress size={16} sx={{ color: '#fff' }} />
            : <AutoAwesomeIcon />}
          disabled={generating || !rawText.trim()}
          onClick={generate}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}
        >
          {generating
            ? 'Genererar...'
            : blocks.length > 0
            ? 'Regenerera block'
            : 'Generera block med AI'}
        </Button>
      </Paper>

      {/* Block list */}
      {blocks.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography fontWeight={600} sx={{ flex: 1 }}>
              {blocks.length} block
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setBlocks(b => [...b, newBlock(b.length)])}
              sx={{ textTransform: 'none', color: 'text.secondary', '&:hover': { color: accent } }}
            >
              Lägg till block
            </Button>
          </Box>

          {blocks.map((block, i) => (
            <BlockCard
              key={block.id || i}
              block={block}
              index={i}
              onChange={updated => setBlocks(b => b.map((x, idx) => idx === i ? updated : x))}
              onDelete={() => setBlocks(b => b.filter((_, idx) => idx !== i))}
            />
          ))}

          {/* Bottom save + launch */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2, pb: 4 }}>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              onClick={() => window.open(`/workshopn/${id}/present?view=speaker`, '_blank')}
              sx={{ textTransform: 'none', borderColor: accent, color: accent }}
            >
              Kör talarstöd
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={saving}
              sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600, minWidth: 100 }}
            >
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Spara'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Empty state */}
      {blocks.length === 0 && !generating && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.disabled' }}>
          <AutoAwesomeIcon sx={{ fontSize: 40, mb: 2, opacity: 0.3 }} />
          <Typography>
            {rawText.trim()
              ? 'Klicka "Generera block med AI" för att strukturera din text.'
              : 'Skriv ditt tal ovan och låt AI strukturera det i block.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
